// Swarm feed — folds coordinator events into one live document per
// coordinator block: jobs/{runId}/swarm/{blockId}. The Agent IO module and
// the pipeline editor draw the swarm from it (rounds → agents, with role,
// mode, status, ms, tokens, preview, who suggested whom, the judge's verdict
// and why the loop stopped). Pure fold + a throttled writer, so it is unit
// testable without Firestore and cheap on the write budget.
//
//   const feed = createSwarmFeed({ db, runId, blockId, label });
//   runCoordinator({ ..., onEvent: feed.onEvent });  await feed.flush();
export function foldSwarmEvent(doc, evt) {
  const d = doc || { status: 'running', rounds: [], tokens: 0 };
  const roundAt = (n) => {
    let r = d.rounds.find((x) => x.n === n);
    if (!r) { r = { n, agents: [], plan: null, judge: null, startedAt: evt.at }; d.rounds.push(r); }
    return r;
  };
  switch (evt.type) {
    case 'start':
      return { ...d, status: 'running', objective: evt.objective, config: evt.config, startedAt: evt.at, rounds: [], tokens: 0 };
    case 'round':
      roundAt(evt.round);
      return { ...d, round: evt.round };
    case 'plan': {
      const r = roundAt(evt.round);
      r.plan = { status: evt.status, ms: evt.ms, tokens: evt.tokens, ...(evt.error ? { error: evt.error } : {}), ...(evt.raw ? { raw: evt.raw } : {}) };
      if (evt.status === 'done') {
        for (const a of evt.agents || []) {
          if (!r.agents.some((x) => x.role === a.role)) r.agents.push({ ...a, status: 'planned' });
        }
      }
      return { ...d, tokens: d.tokens + (evt.tokens || 0) };
    }
    case 'agent': {
      const r = roundAt(evt.round);
      let a = r.agents.find((x) => x.role === evt.role);
      if (!a) { a = { role: evt.role, name: evt.name, mode: evt.mode, task: evt.task || '' }; r.agents.push(a); }
      Object.assign(a, {
        name: evt.name || a.name,
        mode: evt.mode || a.mode,
        status: evt.status,
        suggestedBy: evt.suggestedBy || a.suggestedBy || null,
        ...(evt.task ? { task: evt.task } : {}),
        ...(evt.status === 'running' ? { startedAt: evt.at } : {}),
        ...(evt.ms != null ? { ms: evt.ms } : {}),
        ...(evt.tokens != null ? { tokens: evt.tokens } : {}),
        ...(evt.preview ? { preview: evt.preview } : {}),
        ...(evt.suggest ? { suggest: evt.suggest } : {}),
        ...(evt.error ? { error: evt.error } : {}),
      });
      const add = evt.status === 'done' || evt.status === 'failed' ? (evt.tokens || 0) : 0;
      return { ...d, tokens: d.tokens + add };
    }
    case 'judge': {
      const r = roundAt(evt.round);
      r.judge = { status: evt.status, verdict: evt.verdict || null, note: evt.note || '', ms: evt.ms, tokens: evt.tokens };
      const add = evt.status === 'done' || evt.status === 'failed' ? (evt.tokens || 0) : 0;
      return { ...d, tokens: d.tokens + add };
    }
    case 'stop':
      return { ...d, status: evt.stop === 'approved' ? 'approved' : evt.stop === 'failed' ? 'failed' : 'stopped', stop: evt.stop, why: evt.why, tokens: evt.tokens, ms: evt.ms, finishedAt: evt.at };
    default:
      return d;
  }
}

export function createSwarmFeed({ db, runId, blockId, label = '', meta = {}, minIntervalMs = 700 }) {
  let doc = null;
  let timer = null;
  let last = 0;
  let pending = false;
  const ref = db && runId && blockId ? db.collection('jobs').doc(runId).collection('swarm').doc(blockId) : null;
  // Firestore rejects `undefined` synchronously (the fold leaves e.g.
  // judge.ms unset while running), so prune and never let a telemetry
  // write take the block down with it.
  const write = async () => {
    pending = false;
    last = Date.now();
    if (!ref || !doc) return;
    try {
      const data = JSON.parse(JSON.stringify({ ...doc, ...meta, blockId, label, updatedAt: Date.now() }));
      await ref.set(data, { merge: false });
    } catch { /* telemetry only */ }
  };
  const schedule = () => {
    if (timer) return;
    const wait = Math.max(0, minIntervalMs - (Date.now() - last));
    timer = setTimeout(() => { timer = null; write(); }, wait);
    timer.unref?.();
  };
  return {
    onEvent: async (evt) => {
      doc = foldSwarmEvent(doc, evt);
      pending = true;
      if (evt.type === 'stop' || evt.type === 'start') {
        if (timer) { clearTimeout(timer); timer = null; }
        await write();
      } else schedule();
    },
    flush: async () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (pending) await write();
    },
    snapshot: () => doc,
  };
}
